"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  submitAuditFeedback,
  type FeedbackRating,
} from "../../../lib/api";
import { readToken } from "../../../lib/auth";

/* ================================================================== */
/*  Feedback dialog                                                    */
/*                                                                     */
/*  Slide-in card, bottom-right of the canvas. Shown automatically     */
/*  once per user (15s of visible canvas time), and on demand from     */
/*  the left rail's "Give feedback" button. NOT a backdrop modal:      */
/*  it can appear while a user is exploring their results, and         */
/*  yanking focus away from the thing we want them to fall in love     */
/*  with would defeat the point. Three-emoji rating →                  */
/*  "okay"/"bad" branch into an optional message that lands in the     */
/*  support inbox (with reply-to set to the user). The rating alone    */
/*  is still submitted if they skip or dismiss mid-flow, so silent     */
/*  users still count.                                                 */
/* ================================================================== */

type Step = "rating" | "message" | "done";

const RATING_OPTIONS: Array<{
  rating: FeedbackRating;
  emoji: string;
  label: string;
}> = [
  { rating: "great", emoji: "😍", label: "Great" },
  { rating: "okay", emoji: "🙂", label: "It’s okay" },
  { rating: "bad", emoji: "😕", label: "Not good" },
];

export function FeedbackDialog({
  jobId,
  userEmail,
  onClose,
}: {
  jobId: string;
  /** Shown in the "we'll get back to you at …" confirmation. */
  userEmail?: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>("rating");
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Exactly one submission per dialog lifetime — guards the rating-only
  // flush (skip / dismiss / unmount) against racing the explicit Send.
  const submittedRef = useRef(false);
  const ratingRef = useRef<FeedbackRating | null>(null);

  const submit = useCallback(
    async (chosen: FeedbackRating, text?: string) => {
      if (submittedRef.current) return;
      submittedRef.current = true;
      const token = readToken();
      if (!token) throw new ApiError("Sign in required.", 401);
      try {
        await submitAuditFeedback(token, {
          rating: chosen,
          message: text?.trim() || undefined,
          jobId,
        });
      } catch (err) {
        // Let the caller decide whether the failure is user-visible
        // (explicit Send) or silent (rating-only flush).
        submittedRef.current = false;
        throw err;
      }
    },
    [jobId]
  );

  // Rating-only flush — fire-and-forget, used when the user bails after
  // picking a face but before (or instead of) writing anything.
  const flushRatingOnly = useCallback(() => {
    const chosen = ratingRef.current;
    if (!chosen || submittedRef.current) return;
    void submit(chosen).catch(() => {
      /* best effort — never surface an error for a dialog they dismissed */
    });
  }, [submit]);

  // If the user navigates away mid-flow (e.g. back to the dashboard),
  // still record the rating they picked.
  useEffect(() => flushRatingOnly, [flushRatingOnly]);

  const dismiss = useCallback(() => {
    flushRatingOnly();
    onClose();
  }, [flushRatingOnly, onClose]);

  const pickRating = useCallback(
    (chosen: FeedbackRating) => {
      setRating(chosen);
      ratingRef.current = chosen;
      if (chosen === "great") {
        // Happy path ends here — submit straight away and say thanks.
        void submit(chosen).catch(() => {
          /* best effort */
        });
        setStep("done");
        return;
      }
      setStep("message");
    },
    [submit]
  );

  const send = useCallback(async () => {
    if (!rating || sending) return;
    setSending(true);
    setError(null);
    try {
      await submit(rating, message);
      setStep("done");
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not send your message. Check your connection and try again."
      );
    } finally {
      setSending(false);
    }
  }, [rating, sending, message, submit]);

  // Focus the textarea when the message step opens — the user just
  // clicked a rating, so moving focus is expected, unlike on first
  // appearance (where stealing focus mid-canvas-exploration would be
  // hostile).
  useEffect(() => {
    if (step === "message") textareaRef.current?.focus();
  }, [step]);

  // Auto-close the confirmation so the card doesn't linger as chrome.
  useEffect(() => {
    if (step !== "done") return;
    const timer = window.setTimeout(onClose, rating === "great" ? 2600 : 3600);
    return () => window.clearTimeout(timer);
  }, [step, rating, onClose]);

  const isBad = rating === "bad";

  return (
    <aside
      className="cv-feedback-card"
      role="dialog"
      aria-labelledby="cv-feedback-title"
      onKeyDown={(e) => {
        if (e.key === "Escape" && !sending) dismiss();
      }}
    >
      <header className="cv-feedback-head">
        <span className="cv-feedback-eyebrow">
          {step === "done" ? "Thank you" : "Quick question"}
        </span>
        <button
          type="button"
          className="cv-feedback-close"
          onClick={dismiss}
          disabled={sending}
          aria-label="Dismiss feedback"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </header>

      {step === "rating" ? (
        <>
          <h3 id="cv-feedback-title" className="cv-feedback-title">
            How’s your experience so far?
          </h3>
          <div
            className="cv-feedback-options"
            role="group"
            aria-label="Rate your experience"
          >
            {RATING_OPTIONS.map((option) => (
              <button
                key={option.rating}
                type="button"
                className="cv-feedback-option"
                onClick={() => pickRating(option.rating)}
              >
                <span className="cv-feedback-option-emoji" aria-hidden="true">
                  {option.emoji}
                </span>
                <span className="cv-feedback-option-label">{option.label}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {step === "message" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <h3 id="cv-feedback-title" className="cv-feedback-title">
            {isBad ? "Sorry to hear that." : "Good to know — thanks."}
          </h3>
          <p className="cv-feedback-sub">
            {isBad
              ? "Tell us what went wrong and we’ll personally look into it."
              : "What would make it better? We read every message."}
          </p>
          <label className="cv-field cv-feedback-field">
            <span className="sr-only">Your message</span>
            <textarea
              ref={textareaRef}
              value={message}
              rows={4}
              maxLength={4000}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                isBad
                  ? "What happened? The more detail, the faster we can help."
                  : "What’s missing, confusing, or not working for you?"
              }
            />
          </label>
          {error ? (
            <p className="cv-feedback-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="cv-feedback-actions">
            <button
              type="button"
              className="cv-modal-btn"
              onClick={dismiss}
              disabled={sending}
            >
              {isBad ? "No thanks" : "Skip"}
            </button>
            <button
              type="submit"
              className="cv-modal-btn cv-modal-btn-primary"
              disabled={sending || !message.trim()}
            >
              {sending ? "Sending…" : isBad ? "Send to support" : "Send"}
            </button>
          </div>
        </form>
      ) : null}

      {step === "done" ? (
        <div role="status">
          <h3 id="cv-feedback-title" className="cv-feedback-title">
            {rating === "great"
              ? "Love to hear it — thanks!"
              : "Sent — thank you."}
          </h3>
          {rating !== "great" ? (
            <p className="cv-feedback-sub">
              {isBad && userEmail
                ? `We’ll get back to you at ${userEmail}.`
                : "It goes straight to the founder’s inbox."}
            </p>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}
